// The Jira query builder's model (docs/12 "Jira as a configured source"): the choices the
// builder offers, the JQL they write, and the way back from JQL the builder wrote, so the
// user can edit the text and return to the builder while it still says only what the
// builder can show. Anything else stays raw JQL.
import { parseClauses } from "./jqlParse";

export const STATUS_FILTERS = ["notDone", "toDo", "pick"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

export const ASSIGNEES = ["me", "anyone", "unassigned"] as const;
export type Assignee = (typeof ASSIGNEES)[number];

export const ORDERS = ["priority", "created", "updated", "none"] as const;
export type Order = (typeof ORDERS)[number];

export interface JqlQuery {
  /** Project keys, any of. */
  projects: string[];
  /** Issue type names, any of; none means every type. */
  issueTypes: string[];
  status: StatusFilter;
  /** The statuses when `status` is `pick`, any of. */
  statuses: string[];
  assignee: Assignee;
  /** Labels, any of. */
  labels: string[];
  activeSprint: boolean;
  order: Order;
}

export const EMPTY_QUERY: JqlQuery = {
  projects: [],
  issueTypes: [],
  status: "notDone",
  statuses: [],
  assignee: "anyone",
  labels: [],
  activeSprint: false,
  order: "priority",
};

/** Quick starts: each keeps the projects already chosen. */
export const TEMPLATES = {
  myOpenWork: { assignee: "me", status: "notDone", issueTypes: [], labels: [], order: "priority" },
  unassignedBugs: { assignee: "unassigned", status: "toDo", issueTypes: ["Bug"], labels: [], order: "created" },
  labelledForAgents: { assignee: "anyone", status: "notDone", issueTypes: [], labels: ["agent-ready"], order: "priority" },
} as const satisfies Record<string, Partial<JqlQuery>>;
export type TemplateKey = keyof typeof TEMPLATES;

export function applyTemplate(q: JqlQuery, key: TemplateKey): JqlQuery {
  const t = TEMPLATES[key];
  return { ...q, ...t, issueTypes: [...t.issueTypes], labels: [...t.labels], statuses: [], activeSprint: false };
}

const BARE = /^[A-Za-z0-9_]+$/;
const RESERVED = new Set(["and", "or", "not", "empty", "null", "in", "is", "was", "order", "by", "asc", "desc", "changed"]);

/** A JQL value: bare when it is a plain word, else double-quoted with `"` and `\` escaped. */
export function quote(value: string): string {
  if (BARE.test(value) && !RESERVED.has(value.toLowerCase())) return value;
  return `"${value.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"`;
}

function anyOf(field: string, values: string[]): string | null {
  if (values.length === 0) return null;
  if (values.length === 1) return `${field} = ${quote(values[0]!)}`;
  return `${field} in (${values.map(quote).join(", ")})`;
}

const ORDER_BY: Record<Exclude<Order, "none">, string> = {
  priority: "priority DESC",
  created: "created ASC",
  updated: "updated DESC",
};

const STATUS_CLAUSE: Record<Exclude<StatusFilter, "pick">, string> = {
  notDone: "statusCategory != Done",
  toDo: 'statusCategory = "To Do"',
};

const ASSIGNEE_CLAUSE: Record<Exclude<Assignee, "anyone">, string> = {
  me: "assignee = currentUser()",
  unassigned: "assignee is EMPTY",
};

/** The JQL the builder's choices say, clauses joined by AND. */
export function toJql(q: JqlQuery): string {
  const clauses = [
    anyOf("project", q.projects),
    anyOf("issuetype", q.issueTypes),
    q.status === "pick" ? anyOf("status", q.statuses) : STATUS_CLAUSE[q.status],
    q.assignee === "anyone" ? null : ASSIGNEE_CLAUSE[q.assignee],
    anyOf("labels", q.labels),
    q.activeSprint ? "sprint in openSprints()" : null,
  ].filter((c): c is string => c !== null);
  const where = clauses.join(" AND ");
  if (q.order === "none") return where;
  return `${where}${where ? " " : ""}ORDER BY ${ORDER_BY[q.order]}`;
}

/** The builder's choices for JQL it could have written, or null for anything else. */
export function fromJql(text: string): JqlQuery | null {
  const parsed = parseClauses(text);
  if (!parsed) return null;
  const q: JqlQuery = { ...EMPTY_QUERY, status: "pick", order: "none" };
  const seen = new Set<string>();
  for (const c of parsed.clauses) {
    if (seen.has(c.field)) return null;
    seen.add(c.field);
    switch (c.field) {
      case "project":
        q.projects = c.values;
        break;
      case "issuetype":
        q.issueTypes = c.values;
        break;
      case "labels":
        q.labels = c.values;
        break;
      case "status":
        q.statuses = c.values;
        break;
      case "statuscategory": {
        const value = c.values[0]?.toLowerCase();
        if (c.op === "!=" && value === "done") q.status = "notDone";
        else if (c.op === "=" && value === "to do") q.status = "toDo";
        else return null;
        break;
      }
      case "assignee":
        q.assignee = c.op === "is" ? "unassigned" : "me";
        break;
      case "sprint":
        q.activeSprint = true;
        break;
    }
  }
  if (seen.has("status") && seen.has("statuscategory")) return null;
  const order = (Object.keys(ORDER_BY) as (keyof typeof ORDER_BY)[]).find((k) => ORDER_BY[k].toLowerCase() === parsed.order?.toLowerCase());
  if (parsed.order !== null && !order) return null;
  q.order = order ?? "none";
  return q;
}

/** Jira project keys: a letter, then letters, digits or underscores. */
export const isProjectKey = (key: string): boolean => /^[A-Za-z][A-Za-z0-9_]{0,99}$/.test(key);
